import { useCallback, useMemo, useState } from "react"

import axios from "axios"

import config from "@/config"
import createStore from "@Stores/base-factories"
import hookCollectionStoreFactory from "@Stores/base-factories/store-collection"
import hookCollectionsStoreFactory from "@Stores/base-factories/store-collections"
import { hookCMSBuilderBlockList, hookSiteList } from "@Stores/base-factories/store-list"
import hookSiteSelectedFactory from "@Stores/base-factories/store-site-selection";

import { usePostStore } from "../../store"

const { Provider, useStore } = createStore()
  .extend(hookSiteSelectedFactory('siteId'))
  .extend(hookSiteList('sites'))
  .extend(hookCMSBuilderBlockList('templates'))
  .extend(hookCMSBuilderBlockList('layouts'))
  .extend(hookCMSBuilderBlockList('partials'))
  .extend(hookCollectionStoreFactory('cms-builder/block', { key: 'block' }))
  .extend(({ block: { fetchById }, siteSelected, fetch }) => {
    const [loading, setLoading] = useState(false)

    const fetchBlockById = useCallback(async id => {
      try {
        setLoading(true)
        const block = await fetchById(id)
        return block
      } finally {
        setLoading(false)
      }
    }, [fetchById])

    const fetchBlockVersion = useCallback(async id => {
      const block = await fetchById(id)
      return block?.meta?.version
    }, [fetchById])

    const fetchBlocksTags = useCallback(async () => {
      const { key: siteKey, id: siteId } = siteSelected
      return await fetch({
        url: `/cms-builder/blocks/tags`,
        params: {
          [siteKey]: siteId,
        },
      })
    }, [fetch, siteSelected])

    return useMemo(() => ({
      templateBlock: {
        loading,
        fetchBlockVersion,
        fetchBlockById,
        fetchBlocksTags,
      },
    }), [loading, fetchBlockById, fetchBlocksTags, fetchBlockVersion])
  })
  .extend(hookCollectionsStoreFactory('cms-builder/blocks', { key: 'blocks' }))
  .extend(hookCollectionStoreFactory('cms/post-builder', { key: 'postBuilder' }))
  .extend(hookCollectionsStoreFactory('cms/post-builders', { key: 'postBuilders' }))
  .extendHookVersionFactory('postBuilder')
  .extend(({ siteSelected, postBuilder: { collectionURL, save: handleSave, fetchById, item, history: { preview } }, fetch }) => {
    const { collection: { item: postItem } } = usePostStore()
    const { id: postId } = postItem || {}
    const { id } = item || {}
    const [disableAutoSave, setDisableAutoSave] = useState(localStorage.getItem('disableAutoSave') === 'true')

    const compileCSS = useCallback(async data => {
      const { html, tailwindConfig } = data

      const { data: { css } } = await axios.post(`${config.tailwindProcessor}/compile`, {
        html,
        tailwindConfig,
      })

      return css
    }, [])

    const autoSave = useCallback(async data => {
      if (disableAutoSave) return

      const { key: siteKey, id: siteId } = siteSelected
      const { craft, shouldAudit } = data

      if (preview) {
        return;
      }

      if (!id) {
        return;
      }

      return await fetch({
        method: 'put',
        url: `${collectionURL}/auto-save/${id}`,
        data: {
          [siteKey]: siteId,
          id,
          craft,
          shouldAudit,
        },
      })
    }, [fetch, siteSelected, collectionURL, id, disableAutoSave, preview])

    const toggleAutoSave = useCallback(() => {
      setDisableAutoSave(prev => {
        localStorage.setItem('disableAutoSave', !prev)
        return !prev
      })
    }, [])

    const save = useCallback(async data => {
      const { key: siteKey, id: siteId } = siteSelected
      const { definition: { types: [{ templates: [{ definition: { craft, blocks } }] }] }, meta } = data

      if (preview) {
        return;
      }

      const result = await handleSave({
        [siteKey]: siteId,
        id,
        postId,
        blocks,
        craft,
        meta,
      })

      return fetchById(id || result?.id)
    }, [handleSave, fetchById, siteSelected, postId, id, preview])

    const publish = useCallback(async data => {
      const { key: siteKey, id: siteId } = siteSelected
      const { definition: { types: [{ templates: [{ definition: { craft, blocks } }] }] }, files: [{ html }], tailwindConfig, meta } = data

      if (preview) {
        return;
      }

      const css = await compileCSS({ html, tailwindConfig })

      const result = await handleSave({
        [siteKey]: siteId,
        id,
        postId,
        html,
        css,
        blocks,
        craft,
        meta,
      })

      return fetchById(id || result?.id)
    }, [handleSave, fetchById, siteSelected, postId, id, compileCSS, preview])

    return useMemo(() => ({
      save,
      publish,
      autoSave,
      compileCSS,
      toggleAutoSave,
      disableAutoSave,
    }), [save, publish, autoSave, compileCSS, toggleAutoSave, disableAutoSave])
  })
  .factory()

export {
  Provider as PageBuilderStoreProvider,
  useStore as usePageBuilderStore,
}
